// Libraries
import { TFunction } from "i18next";

// Types
import { SynoptophoreCurrentStepType } from "src/services/exams/types";

// Constants
import { NEAR_TEST_DISTANCE_CM } from "src/constants/enums";

// What the patient is being asked to do
const ACTIVITY_LABELS: Record<string, string> = {
  Instructions: "Listening",
  Resting: "Resting",
  Fixating: "Holding Gaze",
  Holding: "Watching the Target",
  Adjusting: "Adjusting",
  Answering: "Answering",
  Pointing: "Choosing a Target",
};

export const getActivityLabel = (activity: string, t: TFunction): string =>
  t(ACTIVITY_LABELS[activity] ?? activity);

// The headset's title keys are translation keys, with any {{values}} filled in
// from titleValues (e.g. NearDeviation -> "Deviation at Near, 33cm").
export const getStepTitle = (
  step: Pick<SynoptophoreCurrentStepType, "title" | "titleValues">,
  t: TFunction
): string => t(step.title, step.titleValues ?? {});

export type DistanceLabelType = {
  label: string;
  kind: "far" | "near" | "missing";
};

// Null when the module isn't one measured at both distances.
export const getDistanceLabel = (
  step: Pick<SynoptophoreCurrentStepType, "module" | "viewingDistance">,
  t: TFunction
): DistanceLabelType | null => {
  const modules = [
    "ObjectiveAlignment",
    "SubjectiveAngle",
    "FusionalAmplitudes",
  ];

  if (!modules.includes(step.module)) {
    return null;
  }
  if (step.viewingDistance === "Far") {
    return { label: t("Far"), kind: "far" };
  }
  if (step.viewingDistance === "Near") {
    return {
      label: t("Near ({{distance}} cm)", { distance: NEAR_TEST_DISTANCE_CM }),
      kind: "near",
    };
  }
  return { label: t("Distance Not Reported"), kind: "missing" };
};

// One decimal, with an explicit sign so left/right and up/down read at a glance.
// Anything that rounds to zero shows unsigned, never as "-0.0".
export const formatSignedDegrees = (value: number): string => {
  const rounded = Math.abs(value).toFixed(1);
  if (Number(rounded) === 0) return `${rounded}°`;
  return `${value > 0 ? "+" : "-"}${rounded}°`;
};
